/* eslint-disable no-irregular-whitespace */
/* eslint-disable max-lines-per-function */
import { toNDigits } from '@mail/cross-sizes-utils';
import { Button, Spacing } from '@vkontakte/vkui';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import {
    autoreloadAfterSubscription,
    autoreloadAfterSubscriptionTimeout,
    noadsButtonPaymentTitle,
} from 'reactApp/appHelpers/featuresHelpers/features/noadsButton';
import { overquotaAbNoClear } from 'reactApp/appHelpers/featuresHelpers/features/overquotaAbNoClear';
import {
    overquotaAbTariffB,
    overquotaAbTariffBC,
    overquotaAbTariffC,
    overquotaAbTariffD,
    overquotaAbTariffName,
    overquotaAbVariantTariffId,
} from 'reactApp/appHelpers/featuresHelpers/features/overquotaAbTariff';
import { overquotaBlockDate } from 'reactApp/appHelpers/featuresHelpers/features/overquotaBlockDate';
import { overquotaFoldersConfig } from 'reactApp/appHelpers/featuresHelpers/features/overquotaFolders';
import { overquotaNewCommunication } from 'reactApp/appHelpers/featuresHelpers/features/overquotaNewCommunication';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openBuyCheckout, openBuyDisableAds, openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { updateProducts } from 'reactApp/modules/products/products.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { getUserOverquotaState } from 'reactApp/modules/profile/profile.selectors';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import type { RootState } from 'reactApp/store';
import type { Product } from 'reactApp/types/Billing';
import { InformationAlertBanner, InformationBannerType } from 'reactApp/ui/InformationAlertBanner/InformationAlertBanner';
import { OverquotaFolders } from 'reactApp/ui/OverquotaFolders/OverquotaFolders';
import { OverquotaBannerCtrl, sendDwhOverquota } from 'reactApp/ui/OverquotaPopup/new/OverquotaModal.helpers';
import { formatFileHistoryDate } from 'reactApp/utils/formatDate';
import { sendDwh } from 'reactApp/utils/ga';
import { ECategoryGa } from 'reactApp/utils/paymentGa';
import { getUtmString } from 'reactApp/utils/urlHelper';

import styles from './OverquotaBanner.css';

const SOURCE = 'cloud';
const MEDIUM = 'web_union_quota_landing';

const UTM = {
    step_1: {
        buy: getUtmString({
            utm_source: SOURCE,
            utm_medium: MEDIUM,
            utm_campaign: 'overquota_future_block_infoblock',
        }),
        clear: getUtmString({
            utm_source: SOURCE,
            utm_medium: MEDIUM,
            utm_campaign: 'overquota_future_block_infoblock_cleaner',
        }),
    },
    step_2: {
        buy: getUtmString({
            utm_source: SOURCE,
            utm_medium: MEDIUM,
            utm_campaign: 'overquota_future_delete_infoblock',
        }),
        clear: getUtmString({
            utm_source: SOURCE,
            utm_medium: MEDIUM,
            utm_campaign: 'overquota_future_delete_infoblock_cleaner',
        }),
    },
};

// tempexp_19905-start
let isBought = false;
let receiptWatched = false;
let boughtTimer: null | NodeJS.Timeout = null;

const sendRadar = (category: string, action: string, name?: string, paySource?: string) => {
    sendDwh({
        eventCategory: category,
        dwh_enabled: true,
        action,
        dwhData: {
            name,
            paySource,
        },
    });
};
// tempexp_19905-start

export const OverquotaBanner = memo(function OverquotaPopup(): ReactElement | null {
    const storage = useSelector(getCurrentStorage);
    const dispatch = useDispatch();
    const overquota = useSelector(UserQuotaSelectors.getOverQuota);
    const {
        trashSize = 0,
        blockTime = undefined,
        clearTime,
        state,
    }: ReturnType<typeof getUserOverquotaState> = useSelector(getUserOverquotaState);
    const showClearButton = overquotaAbNoClear !== 'b' && !overquotaAbTariffBC && !overquotaFoldersConfig.enabled;
    const productId = overquotaAbVariantTariffId;
    const product = useSelector((state: RootState) => ProductsSelectors.getProductById(state, productId));

    const clearTrash = trashSize > 0;

    const buttonTitle = useMemo(() => {
        if (overquotaNewCommunication && overquotaBlockDate && blockTime && state === 'started') {
            return 'Снять ограничения';
        }
        if (overquotaAbTariffB && state !== 'full_blocked') {
            return 'Увеличить место за 1 ₽';
        }
        if (overquotaAbTariffC && state !== 'full_blocked') {
            return 'Увеличить место со скидкой';
        }
        if (overquotaFoldersConfig.enabled) {
            return 'Снять ограничения';
        }

        return 'Увеличить место';
    }, [state]);

    const name = useMemo(
        () =>
            overquotaAbTariffName && state !== 'full_blocked'
                ? `mnz_overquota_${overquotaAbTariffName}`
                : overquotaAbNoClear
                ? 'overquota_info_alert_cloud'
                : 'overquota_splash_new',
        [state]
    );

    const title = useMemo(() => {
        if (overquotaNewCommunication) {
            if (overquotaBlockDate && blockTime && state === 'started') {
                return `${formatFileHistoryDate(blockTime)} доступ к файлам будет ограничен, но есть решение`;
            }
            if (overquotaBlockDate && clearTime && (state === 'blocked' || state === 'unblocked' || state === 'full_blocked')) {
                return `${formatFileHistoryDate(clearTime)} файлы будут удалены. Как не допустить`;
            }
        }

        if (overquotaAbTariffBC && state !== 'full_blocked') {
            return 'Пока нельзя делиться файлами и загружать новые — закончилось место';
        }

        if (overquotaBlockDate && blockTime && state === 'started') {
            return `${formatFileHistoryDate(blockTime)} доступ к файлам будет заблокирован`;
        }

        if (overquotaBlockDate && clearTime && (state === 'blocked' || state === 'unblocked')) {
            return `${formatFileHistoryDate(clearTime)} ваши файлы будут удалены`;
        }

        if (overquotaFoldersConfig.enabled) {
            return 'Загружать, смотреть, отправлять файлы пока нельзя, но есть решение';
        }

        return <>Вашим файлам не хватает места</>;
    }, [overquotaBlockDate, blockTime, clearTime, state]);

    const content = useMemo(() => {
        if (overquotaNewCommunication) {
            if (clearTrash) {
                return (
                    <>
                        Пространство переполнено — увеличьте его с подпиской Mail Space или очистите{' '}
                        <span>
                            {
                                toNDigits({
                                    bytes: trashSize,
                                    maxDigitsCount: 3,
                                }).value
                            }
                        </span>{' '}
                        в корзине
                    </>
                );
            }

            if (overquotaBlockDate && blockTime && state === 'started') {
                return (
                    <>
                        Пространство переполнено на <span>{overquota.value}</span> — увеличьте его с подпиской Mail Space
                    </>
                );
            }

            if (overquotaBlockDate && clearTime && (state === 'blocked' || state === 'unblocked' || state === 'full_blocked')) {
                return (
                    <>
                        Пространство переполнено на <span>{overquota.value}</span> — увеличьте его с подпиской Mail Space, чтобы сохранить
                        файлы
                    </>
                );
            }
        }

        if (overquotaFoldersConfig.enabled) {
            return (
                <>
                    Единое пространство Облака и Почты переполнено на <span>{overquota.value}</span> — увеличьте его с подпиской Mail Space.
                    Или очистите папки ниже, они занимают больше всего места:
                </>
            );
        }

        if (overquotaAbTariffB && state !== 'full_blocked') {
            // eslint-disable-next-line no-irregular-whitespace
            return `Увеличьте место с подпиской Mail Space. ${product?.space?.value} ваши за 1 ₽`;
        }
        if (overquotaAbTariffC && state !== 'full_blocked') {
            // eslint-disable-next-line no-irregular-whitespace
            return `Увеличьте место с подпиской Mail Space. ${product?.space?.value} ваши со скидкой 50%`;
        }

        if (overquotaBlockDate && (state === 'started' || state === 'blocked' || state === 'unblocked')) {
            return <>Пространство для ваших файлов переполнено. Увеличьте или очистите место</>;
        }

        return <>Скоро они будут удалены. Увеличьте или очистите место, чтобы их сохранить</>;
    }, [overquotaBlockDate, state, product, clearTrash]);

    useEffect(() => {
        if (!productId || product) {
            return;
        }

        dispatch(updateProducts({ productId }));
    }, [productId, product]);

    useEffect(() => {
        sendDwhOverquota({
            action: 'show',
            nameAction: 'overquota_splash_new',
            storage,
            name,
            category: ECategoryGa.informationAlert,
        });
    }, [storage]);

    // tempexp_19905-start
    const onBuyClick = useCallback((product?: Product) => {
        dispatch(
            openBuyCheckout({
                title: noadsButtonPaymentTitle,
                onSuccess: () => {
                    sendRadar('payment', 'fastcheckout_click', '', 'banner_cloud_overquota_structure_space');

                    isBought = true;
                    boughtTimer = setTimeout(() => {
                        boughtTimer = null;

                        if (receiptWatched && autoreloadAfterSubscription) {
                            window.location.reload();
                        }
                    }, autoreloadAfterSubscriptionTimeout);
                },
                onShow: () => {
                    sendRadar('payment', 'fastcheckout_view', '', 'banner_cloud_overquota_structure_space');
                },
                onClose: () => {
                    if (isBought) {
                        receiptWatched = true;

                        if (!boughtTimer && autoreloadAfterSubscription) {
                            window.location.reload();
                        }
                    }
                },
                paySource: 'banner_cloud_overquota_structure_space',
                productId: productId || product?.id,
                hideOtherTariffs: true,
            })
        );
    }, []);
    // tempexp_19905-end

    const onPrimaryHandler = useCallback(() => {
        let search = 'utm_source=cloud_web&utm_medium=infoblock&utm_campaign=overquota_infoblock_new';

        sendDwhOverquota({
            action: 'click',
            storage,
            name,
            category: ECategoryGa.informationAlert,
            control: OverquotaBannerCtrl.add_space,
        });

        if (overquotaBlockDate && blockTime && state === 'started') {
            search = UTM.step_1.buy;
        }

        if (overquotaBlockDate && clearTime && (state === 'blocked' || state === 'unblocked')) {
            search = UTM.step_2.buy;
        }

        if ((overquotaAbTariffBC || overquotaAbTariffD) && state !== 'full_blocked') {
            dispatch(
                openTariffBuy({
                    productId,
                    isMobile: IS_PHONE_BROWSER,
                    title: overquotaAbTariffB
                        ? 'Подписка Mail Space на месяц за 1 ₽'
                        : overquotaAbTariffC
                        ? 'Подписка Mail Space на год со скидкой'
                        : '',
                    paySource: getPaySource(`overquota_info_alert_${overquotaAbTariffName}`),
                })
            );
            return;
        }

        // tempexp_19905-start
        if (overquotaFoldersConfig.enabled) {
            if (overquotaFoldersConfig.tariffs) {
                sendRadar('banner', 'click', 'mnz_overquota_structure_space', '');
                dispatch(
                    openBuyDisableAds({
                        title: noadsButtonPaymentTitle,
                        tariffsNumber: overquotaFoldersConfig.tariffs,
                        activeTab: overquotaFoldersConfig.activeTab,
                        isFrame: false,
                        onBuyClick,
                        paySource: 'mnz_overquota_structure_space',
                    })
                );
            }
            return;
        }
        // tempexp_19905-end

        dispatch(
            historyPush({
                id: '/promo/quota',
                search: `?${search}`,
            })
        );
    }, [productId]);

    const onSecondaryHandler = useCallback(() => {
        let search = `utm_source=cloud_web&utm_medium=infoblock&utm_campaign=overquota_infoblock_new`;

        sendDwhOverquota({
            action: 'click',
            storage,
            name,
            category: ECategoryGa.informationAlert,
            control: OverquotaBannerCtrl.clear,
        });

        if (overquotaBlockDate && blockTime && state === 'started') {
            search = UTM.step_1.clear;
        }

        if (overquotaBlockDate && clearTime && (state === 'blocked' || state === 'unblocked')) {
            search = UTM.step_2.clear;
        }

        dispatch(
            historyPush({
                id: '/promo/quota',
                search: `?${search}`,
                hash: 'cleaner',
            })
        );
    }, []);

    return (
        <InformationAlertBanner
            title={title}
            content={
                <>
                    <div className={styles.content}>{content}</div>
                    {overquotaFoldersConfig.enabled && (
                        <>
                            <Spacing size={12} />
                            <OverquotaFolders />
                            <Spacing size={12} />
                            <Button
                                size={'l'}
                                appearance={'negative'}
                                mode={'primary'}
                                data-test-id={'overquota_bnr-buy-btn'}
                                data-name="primary-bttn"
                                onClick={onPrimaryHandler}
                            >
                                {buttonTitle}
                            </Button>
                        </>
                    )}
                </>
            }
            primaryButtonTitle={overquotaFoldersConfig.enabled ? '' : buttonTitle}
            secondaryButtonTitle={
                showClearButton &&
                (overquotaBlockDate && ((blockTime && state === 'started') || state === 'unblocked') ? 'Очистить' : 'Очистить Облако')
            }
            type={InformationBannerType.alert}
            onPrimaryClick={onPrimaryHandler}
            onSecondaryClick={onSecondaryHandler}
        />
    );
});
